import React from "react";
import { Box, GlobalStyles } from "@mui/material";

/** Tile colors matching Sliding Puzzle Quest app icon. */
export const SLIDING_ICON_TILES = {
    red: "#F26B3A",
    cream: "#F3EDE3",
    green: "#9FD93B",
    blue: "#3B82F6",
    well: "#2A2A2A",
    bg: "#FFFFFF",
};

/** ms per slide step (and for CTA flip half-period). */
export const SLIDING_STEP_MS = 420;
/** Brief hold on the home “icon as is” pose each loop. */
export const SLIDING_HOME_HOLD_MS = 900;

/**
 * Rolling slide cycle — cream acts as the moving empty slot (painted).
 * Returns to home after a full orbit.
 *
 * 0 as is → 1 blue up → 2 green right → 3 red down → 4 blue left →
 * 5 green up → 6 red right → 7 blue down → 8 green left → 9 red up →
 * 10 blue right → 11 green down → 12 red left (= home)
 */
export const SLIDING_ROLL_STEPS = [
    // 0 — icon as is
    { R: [0, 0], C: [0, 1], G: [1, 0], B: [1, 1], Bdy: 0.12 },
    // 1 — blue slides up (swap with cream)
    { R: [0, 0], C: [1, 1], G: [1, 0], B: [0, 1], Bdy: 0 },
    // 2 — green slides right
    { R: [0, 0], C: [1, 0], G: [1, 1], B: [0, 1], Bdy: 0 },
    // 3 — red slides down
    { R: [1, 0], C: [0, 0], G: [1, 1], B: [0, 1], Bdy: 0 },
    // 4 — blue left
    { R: [1, 0], C: [0, 1], G: [1, 1], B: [0, 0], Bdy: 0 },
    // 5 — green up
    { R: [1, 0], C: [1, 1], G: [0, 1], B: [0, 0], Bdy: 0 },
    // 6 — red right
    { R: [1, 1], C: [1, 0], G: [0, 1], B: [0, 0], Bdy: 0 },
    // 7 — blue down
    { R: [1, 1], C: [0, 0], G: [0, 1], B: [1, 0], Bdy: 0 },
    // 8 — green left
    { R: [1, 1], C: [0, 1], G: [0, 0], B: [1, 0], Bdy: 0 },
    // 9 — red up
    { R: [0, 1], C: [1, 1], G: [0, 0], B: [1, 0], Bdy: 0 },
    // 10 — blue right
    { R: [0, 1], C: [1, 0], G: [0, 0], B: [1, 1], Bdy: 0 },
    // 11 — green down
    { R: [0, 1], C: [0, 0], G: [1, 0], B: [1, 1], Bdy: 0 },
    // 12 — red left → home layout
    { R: [0, 0], C: [0, 1], G: [1, 0], B: [1, 1], Bdy: 0.12 },
];

export function slidingIconLoopMs() {
    // home hold + one duration per step transition (steps 0→1 … 11→12), then hold again is start of next loop
    return SLIDING_HOME_HOLD_MS + SLIDING_STEP_MS * (SLIDING_ROLL_STEPS.length - 1);
}

function easeInOut(t) {
    return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

function lerpCell(a, b, t) {
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

/** CTA timeline — matches SlidingBannerCreative preview. */
export const CTA_SLIDE_HOLD_MS = 1000;
export const CTA_SLIDE_EXIT_MS = 1100;

function easeOutCubic(t) {
    return 1 - (1 - t) ** 3;
}

/**
 * Pose at time ms into the rolling loop.
 * @returns {{
 *   R:[number,number], C:[number,number], G:[number,number], B:[number,number], Bdy:number,
 *   ctaLabel:'SLIDE'|'PLAY',
 *   ctaShowSlide:boolean,
 *   ctaShowPlay:boolean,
 *   ctaExitT:number
 * }}
 */
export function slidingIconPoseAt(ms) {
    const loop = slidingIconLoopMs();
    let m = ((ms % loop) + loop) % loop;

    let stepIndex = 0;
    let blend = 0;

    if (m < SLIDING_HOME_HOLD_MS) {
        stepIndex = 0;
        blend = 0;
    } else {
        const animMs = m - SLIDING_HOME_HOLD_MS;
        const raw = animMs / SLIDING_STEP_MS;
        stepIndex = Math.min(SLIDING_ROLL_STEPS.length - 2, Math.floor(raw));
        blend = easeInOut(Math.min(1, raw - stepIndex));
    }

    const from = SLIDING_ROLL_STEPS[stepIndex];
    const to = SLIDING_ROLL_STEPS[stepIndex + 1] || from;

    const pose = {
        R: lerpCell(from.R, to.R, blend),
        C: lerpCell(from.C, to.C, blend),
        G: lerpCell(from.G, to.G, blend),
        B: lerpCell(from.B, to.B, blend),
        Bdy: (from.Bdy || 0) + ((to.Bdy || 0) - (from.Bdy || 0)) * blend,
    };

    const exitEnd = CTA_SLIDE_HOLD_MS + CTA_SLIDE_EXIT_MS;
    let ctaLabel = "SLIDE";
    let ctaShowSlide = true;
    let ctaShowPlay = false;
    let ctaExitT = 0;

    if (m < CTA_SLIDE_HOLD_MS) {
        ctaExitT = 0;
    } else if (m < exitEnd) {
        ctaExitT = easeOutCubic((m - CTA_SLIDE_HOLD_MS) / CTA_SLIDE_EXIT_MS);
    } else {
        ctaShowSlide = false;
        ctaShowPlay = true;
        ctaLabel = "PLAY";
        ctaExitT = 1;
    }

    return { ...pose, ctaLabel, ctaShowSlide, ctaShowPlay, ctaExitT };
}

/**
 * Live CSS preview of the rolling 2×2 tile-slide icon.
 */
export default function SlidingAnimatedIcon({ size = 38 }) {
    const pad = Math.max(2, Math.round(size * 0.08));
    const gap = Math.max(1.5, size * 0.045);
    const tile = (size - pad * 2 - gap) / 2;
    const radius = Math.max(3, tile * 0.22);
    const loopSec = slidingIconLoopMs() / 1000;
    const stride = tile + gap;

    const tileBase = {
        position: "absolute",
        width: tile,
        height: tile,
        borderRadius: `${radius}px`,
        boxShadow: "0 1px 2px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.35)",
        willChange: "transform",
        left: pad,
        top: pad,
    };

    const pxKeyframes = buildPxKeyframesCss(stride);

    return (
        <>
            <GlobalStyles styles={pxKeyframes} />
            <Box
                sx={{
                    width: size,
                    height: size,
                    flexShrink: 0,
                    borderRadius: `${Math.round(size * 0.2)}px`,
                    bgcolor: SLIDING_ICON_TILES.bg,
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(28,25,23,0.18)",
                    border: "1px solid rgba(28,25,23,0.08)",
                    boxSizing: "border-box",
                }}
            >
                {/* Dark well under home BR */}
                <Box
                    sx={{
                        position: "absolute",
                        left: pad + stride,
                        top: pad + stride,
                        width: tile,
                        height: tile,
                        borderRadius: `${radius}px`,
                        bgcolor: SLIDING_ICON_TILES.well,
                    }}
                />

                <Box
                    sx={{
                        ...tileBase,
                        bgcolor: SLIDING_ICON_TILES.cream,
                        backgroundImage:
                            "linear-gradient(145deg, #FFFCFA 0%, #F3EDE3 55%, #E8DFD0 100%)",
                        animation: `slidingAdIconCpx ${loopSec}s linear infinite`,
                        zIndex: 1,
                    }}
                />
                <Box
                    sx={{
                        ...tileBase,
                        bgcolor: SLIDING_ICON_TILES.red,
                        backgroundImage:
                            "linear-gradient(145deg, #FF8A5C 0%, #F26B3A 50%, #E85A2A 100%)",
                        animation: `slidingAdIconRpx ${loopSec}s linear infinite`,
                        zIndex: 2,
                    }}
                />
                <Box
                    sx={{
                        ...tileBase,
                        bgcolor: SLIDING_ICON_TILES.green,
                        backgroundImage:
                            "linear-gradient(145deg, #B8F04A 0%, #9FD93B 50%, #84CC16 100%)",
                        animation: `slidingAdIconGpx ${loopSec}s linear infinite`,
                        zIndex: 2,
                    }}
                />
                <Box
                    sx={{
                        ...tileBase,
                        bgcolor: SLIDING_ICON_TILES.blue,
                        backgroundImage:
                            "linear-gradient(145deg, #60A5FA 0%, #3B82F6 50%, #2563EB 100%)",
                        animation: `slidingAdIconBpx ${loopSec}s linear infinite`,
                        zIndex: 3,
                    }}
                />
            </Box>
        </>
    );
}

function buildPxKeyframesCss(stride) {
    const loop = slidingIconLoopMs();
    const holdPct = (SLIDING_HOME_HOLD_MS / loop) * 100;
    const stepPct = (SLIDING_STEP_MS / loop) * 100;

    const pctForStep = (i) => (i === 0 ? 0 : holdPct + (i - 1) * stepPct);

    const make = (name, getter, withBdy) => {
        const lines = SLIDING_ROLL_STEPS.map((step, i) => {
            const [row, col] = getter(step);
            const x = col * stride;
            const y = row * stride + (withBdy ? (step.Bdy || 0) * stride : 0);
            if (i === 0) {
                return `  0%, ${holdPct.toFixed(2)}% { transform: translate(${x}px, ${y}px); }`;
            }
            return `  ${pctForStep(i).toFixed(2)}% { transform: translate(${x}px, ${y}px); }`;
        });
        const [hr, hc] = getter(SLIDING_ROLL_STEPS[0]);
        const hy = hr * stride + (withBdy ? (SLIDING_ROLL_STEPS[0].Bdy || 0) * stride : 0);
        lines.push(`  100% { transform: translate(${hc * stride}px, ${hy}px); }`);
        return `@keyframes ${name} {\n${lines.join("\n")}\n}`;
    };

    return `
${make("slidingAdIconRpx", (s) => s.R, false)}
${make("slidingAdIconCpx", (s) => s.C, false)}
${make("slidingAdIconGpx", (s) => s.G, false)}
${make("slidingAdIconBpx", (s) => s.B, true)}
`;
}
